import { z } from "zod";

/**
 * LAF-280 extraction — LogicInspectorSchema moved to its own cycle-free module
 * so that save-roll.schema.ts (and any future low-level schema) can import it
 * without pulling in the mechanical-events → events.ts cycle that lives in
 * resolution.ts.
 *
 * Consumers import LogicInspector/LogicInspectorSchema directly from here;
 * resolution.ts imports it for local use in ExecutionPlanSchema (no re-export).
 */

/**
 * A first-class, declarative predicate over the dice *result structure* —
 * evaluated by `evaluateResultPredicate` (lib/kernel/evaluators/result-predicate.ts).
 * Closed union: a new predicate REQUIRES a new arm here + a new interpreter arm
 * (the interpreter's exhaustive `never` guard enforces this at compile time), so
 * a future predicate (e.g. Chromatic Orb doubles) cannot be hacked in around the
 * type system.
 */
export const ResultPredicateSchema = z.discriminatedUnion("kind", [
  // Reads an rpg-dice-roller crit modifier flag on a kept die (sides-agnostic,
  // set by Axis-A's cs/cf notation). LIVE consumer: crit-detection.
  z.object({
    kind: z.literal("crit-marker"),
    marker: z.enum(["critical-success", "critical-failure"]),
  }),
  // Any two rolled dice show equal face values. (Chromatic Orb → extra bounce.)
  z.object({ kind: z.literal("doubles") }),
  // Any rolled die's face ∈ faces. (Recharge N–M, e.g. faces: [5, 6].)
  z.object({
    kind: z.literal("die-in-set"),
    faces: z.array(z.number().int()).min(1),
  }),
  // Any rolled die's face is within an inclusive range. (Death save
  // range-as-natural-20 effects, e.g. Champion Survivor's 18-20.)
  z.object({
    kind: z.literal("die-in-range"),
    min: z.number().int(),
    max: z.number().int().optional(),
  }),
  // Count of rolled dice showing `face` is ≥ `atLeast`. (Count sixes, etc.)
  z.object({
    kind: z.literal("count-faces"),
    face: z.number().int(),
    atLeast: z.number().int().min(1),
  }),
]);
export type ResultPredicate = z.infer<typeof ResultPredicateSchema>;

export const LogicInspectorSchema = z.discriminatedUnion("type", [
  z.object({
    outcomeId: z.string(),
    type: z.literal("comparison"),
    operator: z.enum(["gte", "lte", "eq", "default"]),
    value: z.number(),
  }),
  z.object({
    outcomeId: z.string(),
    type: z.literal("function"),
    functionId: z.string(),
  }),
  z.object({
    outcomeId: z.string(),
    type: z.literal("result-predicate"),
    predicate: ResultPredicateSchema,
  }),
]);

export type LogicInspector = z.infer<typeof LogicInspectorSchema>;
